/**
* Local Storage Module
* Wrapper for interfacing w/ HTML5 localStorage. (De-)serializes values for storage, scopes keys to 'SCM' namespace.
* 
* Supports read/write from another domain's local storage if that domain
* has set up a hub to support it.
*
* - Cross-Domain functionailty ** REQUIRES ** the cross-storage library (https://github.com/zendesk/cross-storage)
* - Cross-Domain functionality utilizes Promises, so a Promise polyfill is ** HIGHLY RECOMMENDED ** for full browser support.
*/
var SCM = SCM || {};
SCM.Utilities = SCM.Utilities || {};

SCM.Utilities.LocalStorage = (function(){
  'use strict';

  var _namespace = 'SCM';
  var _storageAdapter = window.localStorage;
    var _cacheUpdateLog = [];
    var _syncStartedLog = [];
    var _crossStorageInstances = [];

    /**
    * Returns module API
    * @returns {object}
    */
    return {
        getItem: getItem,
        setItem: setItem,
        removeItem: removeItem,
        getItemCrossDomainNoCache: getItemCrossDomainNoCache,
        getItemCrossDomain: getItemCrossDomain,
        setItemCrossDomain: setItemCrossDomain,
        removeItemCrossDomain: removeItemCrossDomain
    };
	

  /**
  * Serializes and stores in storageAdapter
  *
  * @param {string} key - The storage Key
  * @param {object} data - The storage Value
  * @param {object} adapter - The localStorage adapter
  */
  function setItem(key, value, adapter){
    var adapter = adapter ? adapter : _storageAdapter;
    adapter.setItem(_namespace + '.' + key, JSON.stringify(value));
  }

  /**
  * Retrieves and de-serializes from storageAdapter
  *
  * @param {string} key - The storage Key
  * @param {object} adapter - The localStorage adapter
  */
  function getItem(key, adapter){
    var adapter = adapter ? adapter : _storageAdapter;
    var item = adapter.getItem(_namespace+'.'+key);
    return JSON.parse(item);
  }

  /**
  * Removes the object from storageAdapter
  *
  * @param {string} key - The session storage Key to retrive
  */
  function removeItem(key, adapter){
    var adapter = adapter ? adapter : _storageAdapter;
    adapter.removeItem(_namespace+'.'+key);
  } 

    /**
    * Serializes and stores cross-domain using the provided URL.
    *
    * @param {string} key - The storage Key
    * @param {object} data - The storage Value
    * @param {string} crossStorageHubUrl - The URL for the cross-domain storage hub
     * @returns a promise that is fulfilled on success, or rejected if any errors setting the key occurred, or the request timed out.
    */
  function setItemCrossDomain(key, value, crossStorageHubUrl) {
      var localCacheKey = getHostnameFromUrl(crossStorageHubUrl) + '.' + key;
      // Immediately store the value in our local cache so that it is available for any subsequent 'get' requests
      // during this page load
      SCM.Utilities.LocalStorage.setItem(localCacheKey, value);
      _markLocalCacheAsUpdated(crossStorageHubUrl, key);
      var rvPromise;
      if (CrossStorageClient === undefined) {
          console.error('CrossStorageClient library is not available.  Falling back to localStorage');
          rvPromise = Promise.reject('CrossStorageClient library is not available');
      } else {
          var crossDomainStorage = getCrossStorageClientInstance(crossStorageHubUrl);
          rvPromise = crossDomainStorage.onConnect()
              .then(function () {
                  return crossDomainStorage.set(_namespace + '.' + key, JSON.stringify(value));
              })
              ['catch'](function (err) {
                  console.error('Failed to retrieve / set cross-domain localStorage :: ' + err.message);
              });
      }
      return rvPromise;
  }

    /**
    * Retrieves and de-serializes from a cross-domain storage hub
    *
    * @param {string} key - The storage Key
    * @param {string} crossStorageHubUrl - The URL for the cross-domain storage hub
     * @returns a promise that is settled on hub response or timeout. On success, it is fulfilled with the value of the key.
    */
  function getItemCrossDomainNoCache(key, crossStorageHubUrl) {
      var localCacheKey = getHostnameFromUrl(crossStorageHubUrl) + '.' + key;
      var rvPromise;
      if (CrossStorageClient === undefined) {
          console.error('CrossStorageClient library is not available.');
          rvPromise = Promise.reject('CrossStorageClient library is not available');
      } else {
          var crossDomainStorage = getCrossStorageClientInstance(crossStorageHubUrl);
          rvPromise = crossDomainStorage.onConnect()
              .then(function (res) {
                  return crossDomainStorage.get(_namespace + '.' + key);
              })
              .then(function (res) {
                  return JSON.parse(res);
              })
              ['catch'](function (err) {
                  console.error('Failed to retrieve from cross-domain localStorage (nocache) :: ' + err.message);
                  if (err.message.includes('Could not access localStorage in hub')) {
                      console.warn('It seems that the browser is set to block third-party cookies and data.  Cross-domain localStorage cannot function in this state.');
                  }
                  throw err;
              });
      }
      return rvPromise;
  }


    /**
    * Retrieves and de-serializes from a cross-domain storage hub, using our local cache for speed
    *
    * @param {string} key - The storage Key
    * @param {string} crossStorageHubUrl - The URL for the cross-domain storage hub
     * @returns the locally cached value of the provided key
    */
  function getItemCrossDomain(key, crossStorageHubUrl) {
      var localCacheKey = getHostnameFromUrl(crossStorageHubUrl) + '.' + key;
      if (CrossStorageClient === undefined) {
          console.error('CrossStorageClient library is not available.  Falling back to localStorage');
      } else {
          if (!_isLocalCacheUpdated(crossStorageHubUrl, key) && !_isSyncStarted(crossStorageHubUrl, key)) {
              _markSyncStarted(crossStorageHubUrl, key);
              getItemCrossDomainNoCache(key, crossStorageHubUrl)
                  .then(function(res) {
                      if (res) {
                          // If we already updated our local cache, e.g. during setCrossDomainItem(),
                          // then we don't want to update it again with an outdated value
                          if (!_isLocalCacheUpdated(crossStorageHubUrl, key)) {
                              _markLocalCacheAsUpdated(crossStorageHubUrl, key);
                              SCM.Utilities.LocalStorage.setItem(localCacheKey, res);
                          }
                      }
                  })
                  ['catch'](function(err) {
                      console.error('Failed to retrieve from cross-domain localStorage :: ' + err.message);
                  });
          }
      }
      // Return the locally cached value to ensure that we don't slow down widget loading by waiting for the result.
      // Note that at this point, an async cache update from above may or may not have
      // completed yet, so we might be returning a slightly out-dated value.
      return SCM.Utilities.LocalStorage.getItem(localCacheKey);
  }

    /**
    * Removes the object from storageAdapter
    *
    * @param {string} key - The session storage Key to remove
    * @param {string} crossStorageHubUrl - The URL for the cross-domain storage hub
     * @returns a promise that is settled on hub response or timeout.
    */
  function removeItemCrossDomain(key, crossStorageHubUrl) {
      if (CrossStorageClient === undefined) {
          console.error('CrossStorageClient library is not available.  Falling back to localStorage');
      } else {
          var crossDomainStorage = getCrossStorageClientInstance(crossStorageHubUrl);
          crossDomainStorage.onConnect()
              .then(function () {
                  return crossDomainStorage.del(_namespace + '.' + key);
              })
              ['catch'](function (err) {
                  console.error('Failed to save to cross-domain localStorage :: ' + err.message);
              });
      }
      SCM.Utilities.LocalStorage.removeItem(getHostnameFromUrl(crossStorageHubUrl) + '.' + key);
  }

  function getHostnameFromUrl(url) {
      return $('<a>', { href: url }).prop('hostname');
  }

    /**
     * Create if it doesn't exist and return the single instance of the CrossStorageClient for a given endpoint.
     * 
     * @param {string} crossStorageHubUrl 
     * @returns {CrossStorageClient} the single instance for the given hub url
     */
    function getCrossStorageClientInstance(crossStorageHubUrl) {
        for (var i = 0; i < _crossStorageInstances.length; i++) {
            if (_crossStorageInstances[i].hubUrl === crossStorageHubUrl) {
                return _crossStorageInstances[i].storage;
            }
        }
        var newStorage = new CrossStorageClient(crossStorageHubUrl, { timeout: 15000 });
        _crossStorageInstances.push({ hubUrl: crossStorageHubUrl, storage: newStorage });
        return newStorage;
    }

    function _markLocalCacheAsUpdated(crossStorageHubUrl, key) {
        var cacheLogString = crossStorageHubUrl + '::' + key;
        if (_cacheUpdateLog.indexOf(cacheLogString) === -1) {
            _cacheUpdateLog.push(cacheLogString);
        }
    }

    function _isLocalCacheUpdated(crossStorageHubUrl, key) {
        var cacheLogString = crossStorageHubUrl + '::' + key;
        return _cacheUpdateLog.indexOf(cacheLogString) !== -1;
    }

    function _markSyncStarted(crossStorageHubUrl, key) {
        var cacheLogString = crossStorageHubUrl + '::' + key;
        if (_syncStartedLog.indexOf(cacheLogString) === -1) {
            _syncStartedLog.push(cacheLogString);
        }
    }

    function _isSyncStarted(crossStorageHubUrl, key) {
        var cacheLogString = crossStorageHubUrl + '::' + key;
        return _syncStartedLog.indexOf(cacheLogString) !== -1;
    }

})();